import type { StaffFirebaseRole } from '../firebase'

/** Customer reason on the case (`category` / `categoryId`). Not the desk queue. */
export type SupportReasonId =
  | 'batteryStation'
  | 'roadAssist'
  | 'accident'
  | 'hijackTheft'
  | 'generalEnquiries'
  | 'wantToJoin'
  | 'otherSupport'

/** Routing desk (`queueId`). Onboarding is I Want To Join only. */
export type SupportQueueId = 'charging' | 'workshop' | 'finance' | 'app' | 'onboarding'

/** @deprecated use SupportQueueId — kept so older imports compile */
export type SupportCategory = SupportQueueId

export type SupportPriority = 'P0' | 'P1' | 'P2' | 'P3'

export const SUPPORT_REASON_META: Record<SupportReasonId, { label: string; queueId: SupportQueueId }> = {
  batteryStation: { label: 'Battery / Station', queueId: 'charging' },
  roadAssist: { label: 'Road Assist', queueId: 'workshop' },
  accident: { label: 'Accident', queueId: 'workshop' },
  hijackTheft: { label: 'Hijacking / Theft', queueId: 'workshop' },
  generalEnquiries: { label: 'General Enquiries', queueId: 'finance' },
  wantToJoin: { label: 'I Want To Join', queueId: 'onboarding' },
  otherSupport: { label: 'Other Support', queueId: 'app' },
}

export const SUPPORT_QUEUE_META: Record<SupportQueueId, { label: string }> = {
  charging: { label: 'Charging' },
  workshop: { label: 'Workshop' },
  finance: { label: 'Finance' },
  app: { label: 'App' },
  onboarding: { label: 'Onboarding' },
}

export const SUPPORT_QUEUE_ORDER: SupportQueueId[] = ['charging', 'workshop', 'finance', 'app', 'onboarding']

/** @deprecated use SUPPORT_QUEUE_ORDER */
export const SUPPORT_CATEGORY_ORDER = SUPPORT_QUEUE_ORDER

export const SUPPORT_CATEGORY_META: Record<SupportCategory, { label: string; description: string }> = {
  finance: { label: 'Finance', description: 'Payment, billing, and payouts' },
  charging: { label: 'Charging', description: 'EV charging and station issues' },
  workshop: { label: 'Workshop', description: 'Vehicle maintenance and repairs' },
  app: { label: 'App', description: 'Driver app and general support' },
  onboarding: { label: 'Onboarding', description: 'I Want To Join' },
}

export const RESOLUTION_CODES = [
  { id: 'issue_resolved', label: 'Issue resolved' },
  { id: 'workaround_given', label: 'Workaround given' },
  { id: 'duplicate', label: 'Duplicate' },
  { id: 'no_response', label: 'No response' },
  { id: 'referred_workshop', label: 'Referred to workshop' },
  { id: 'referred_finance', label: 'Referred to finance' },
  { id: 'false_alarm', label: 'False alarm' },
  { id: 'other', label: 'Other' },
] as const

const REASON_IDS = new Set<string>(Object.keys(SUPPORT_REASON_META))

const LEGACY_REASON: Record<string, SupportReasonId> = {
  charging: 'batteryStation',
  finance: 'generalEnquiries',
  workshop: 'roadAssist',
  app: 'otherSupport',
  other: 'otherSupport',
}

export type SupportCategoryFilter = 'all' | SupportQueueId

function asRecord(ticket: unknown): Record<string, unknown> {
  if (ticket && typeof ticket === 'object') return ticket as Record<string, unknown>
  return {}
}

export function isSupportReasonId(raw: unknown): raw is SupportReasonId {
  return REASON_IDS.has(String(raw || ''))
}

/** Reason enum for display. Never maps unknown Flutter reasons to `app`. */
export function reasonIdFromTicket(ticket: unknown): string {
  const d = asRecord(ticket)
  const raw = String(d.categoryId || d.category || '').trim()
  if (!raw) return ''
  if (isSupportReasonId(raw)) return raw
  const legacy = LEGACY_REASON[raw.toLowerCase()]
  if (legacy) return legacy
  return raw
}

export function reasonLabel(ticketOrRaw: unknown): string {
  if (ticketOrRaw && typeof ticketOrRaw === 'object') {
    const id = reasonIdFromTicket(ticketOrRaw)
    if (isSupportReasonId(id)) return SUPPORT_REASON_META[id].label
    return id || '—'
  }
  const raw = String(ticketOrRaw || '').trim()
  if (isSupportReasonId(raw)) return SUPPORT_REASON_META[raw].label
  const legacy = LEGACY_REASON[raw.toLowerCase()]
  if (legacy) return SUPPORT_REASON_META[legacy].label
  return raw || '—'
}

export function queueIdFromTicket(ticket: unknown): SupportQueueId {
  const d = asRecord(ticket)
  const q = String(d.queueId || '').toLowerCase()
  if (q === 'charging' || q === 'workshop' || q === 'finance' || q === 'app' || q === 'onboarding') return q

  const reason = reasonIdFromTicket(ticket)
  if (isSupportReasonId(reason)) return SUPPORT_REASON_META[reason].queueId

  const legacy = String(d.category || '').toLowerCase()
  if (legacy === 'charging') return 'charging'
  if (legacy === 'finance') return 'finance'
  if (legacy === 'workshop') return 'workshop'
  if (legacy === 'app' || legacy === 'other') return 'app'
  // Unknown without queueId: still show on App desk rather than hiding the case.
  return 'app'
}

export function queueLabel(queueId: unknown): string {
  const q = String(queueId || '')
  if (q === 'charging' || q === 'workshop' || q === 'finance' || q === 'app' || q === 'onboarding') {
    return SUPPORT_QUEUE_META[q].label
  }
  return q || '—'
}

export function priorityFromTicket(ticket: unknown): SupportPriority {
  const d = asRecord(ticket)
  const p = String(d.priority || '').toUpperCase()
  if (p === 'P0' || p === 'P1' || p === 'P2' || p === 'P3') return p
  const reason = reasonIdFromTicket(ticket)
  if (reason === 'accident' || reason === 'hijackTheft') return 'P0'
  if (reason === 'roadAssist') return 'P1'
  if (reason === 'generalEnquiries' || reason === 'wantToJoin') return 'P3'
  return 'P2'
}

export function queuesVisibleToRole(role: StaffFirebaseRole | string | undefined): SupportQueueId[] | 'all' {
  const r = String(role || 'admin').toLowerCase()
  if (r === 'finance') return ['finance', 'app']
  if (r === 'workshop') return ['workshop', 'charging', 'app']
  return 'all'
}

export function ticketVisibleToRole(ticket: unknown, role: StaffFirebaseRole | string | undefined): boolean {
  const allowed = queuesVisibleToRole(role)
  if (allowed === 'all') return true
  return allowed.includes(queueIdFromTicket(ticket))
}

/**
 * Queue id from a reason/category string. Do not use this for reason labels —
 * `batteryStation` is Battery / Station, not App.
 */
export function normalizeSupportCategory(raw: unknown): SupportQueueId {
  if (raw && typeof raw === 'object') return queueIdFromTicket(raw)
  return queueIdFromTicket({ category: raw, categoryId: raw })
}

export function supportCategoryLabel(raw: unknown): string {
  if (raw && typeof raw === 'object') return reasonLabel(raw)
  const asQueue = String(raw || '')
  if (
    asQueue === 'charging' ||
    asQueue === 'workshop' ||
    asQueue === 'finance' ||
    asQueue === 'app' ||
    asQueue === 'onboarding'
  ) {
    return SUPPORT_QUEUE_META[asQueue].label
  }
  return reasonLabel(raw)
}

export function groupTicketsByCategory<T>(
  tickets: T[],
  order: SupportQueueId[] = SUPPORT_QUEUE_ORDER,
): { category: SupportQueueId; label: string; tickets: T[] }[] {
  const buckets = new Map<SupportQueueId, T[]>()
  for (const cat of order) buckets.set(cat, [])
  for (const ticket of tickets) {
    const cat = queueIdFromTicket(ticket)
    buckets.get(cat)?.push(ticket)
  }
  return order.map((category) => ({
    category,
    label: SUPPORT_QUEUE_META[category].label,
    tickets: buckets.get(category) || [],
  }))
}
